import type { Alert } from "@/types/api";

export type SuccessStatus = "success" | "none" | "unknown";
export type AuthTrend = "flat" | "escalating" | "declining" | "unknown";

export interface AuthSourceRollup {
  ip: string;
  failureCount: number;
  asn: string | null;
  organization: string | null;
  country: string | null;
  firstSeenHere: string | null;
}

export interface BruteForcePresentation {
  isBruteForce: boolean;
  successStatus: SuccessStatus;
  successfulAccount: string | null;
  successfulIp: string | null;
  successfulAt: string | null;
  failureCount: number;
  sources: AuthSourceRollup[];
  sourceCount: number;
  targetHost: string | null;
  targetService: string | null;
  authMethodAccepted: string | null;
  passwordAuthDisabled: boolean;
  usernames: string[];
  distinctUsernameCount: number | null;
  trend: AuthTrend;
  volumeHistory: number[];
  durationDays: number;
  firingCount: number;
  currentWindowOnly: boolean;
  relatedDetections: string[];
  missingFields: string[];
}

const BRUTE_FORCE_RULE_IDS = new Set(["BF-001", "BF-002", "ID-001"]);

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Read either an Athena dotted alias or a nested OCSF object path. */
function field(source: Record<string, unknown>, ...paths: string[]): unknown {
  for (const path of paths) {
    if (source[path] !== undefined && source[path] !== null && source[path] !== "") {
      return source[path];
    }
    let cursor: unknown = source;
    for (const part of path.split(".")) {
      const current = record(cursor);
      if (!current || current[part] === undefined || current[part] === null) {
        cursor = undefined;
        break;
      }
      cursor = current[part];
    }
    if (cursor !== undefined && cursor !== null && cursor !== "") return cursor;
  }
  return undefined;
}

function text(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number") return String(value);
  return null;
}

function count(value: unknown, fallback = 0): number {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
}

function booleanOrNull(value: unknown): boolean | null {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (["true", "success", "yes"].includes(value.toLowerCase())) return true;
    if (["false", "none", "no"].includes(value.toLowerCase())) return false;
  }
  return null;
}

function durationDays(first: string, last: string): number {
  const start = Date.parse(first);
  const end = Date.parse(last);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return Math.max(1, Math.ceil((end - start) / 86_400_000));
}

function trendFromContext(context: Record<string, unknown>): AuthTrend {
  const explicit = text(context.trend)?.toLowerCase();
  if (explicit === "flat" || explicit === "escalating" || explicit === "declining") {
    return explicit;
  }
  const history = Array.isArray(context.volume_history)
    ? context.volume_history.map((v) => count(record(v)?.count ?? v)).filter((v) => v > 0)
    : [];
  if (history.length < 3) return "unknown";
  const previous = history.slice(0, -1);
  const baseline = previous.reduce((sum, value) => sum + value, 0) / previous.length;
  const latest = history[history.length - 1] ?? baseline;
  if (latest >= baseline * 1.5) return "escalating";
  if (latest <= baseline * 0.5) return "declining";
  return "flat";
}

function volumeHistoryFromContext(context: Record<string, unknown>): number[] {
  return Array.isArray(context.volume_history)
    ? context.volume_history.map((value) => count(record(value)?.count ?? value)).filter((value) => value > 0)
    : [];
}

function sourceRollups(alert: Alert, currentWindowOnly: boolean): AuthSourceRollup[] {
  // Legacy tenant-scoped alerts accumulated entities across windows. Do not
  // present those as current attackers: only structured/current-cycle evidence
  // is eligible for the ranked list.
  if (!currentWindowOnly) return [];

  const byIp = new Map<string, AuthSourceRollup>();
  for (const rawEvent of alert.matched_events ?? []) {
    const event = record(rawEvent);
    if (!event) continue;
    const ip = text(field(event, "src_endpoint.ip", "src_ip", "source_ip", "ip"));
    if (!ip) continue;
    const existing = byIp.get(ip);
    const failures = count(field(event, "failure_count", "failures", "cnt", "count"), 1);
    const candidate: AuthSourceRollup = {
      ip,
      failureCount: failures,
      asn: text(field(event, "src_endpoint.asn", "asn", "asn_number")),
      organization: text(field(event, "src_endpoint.org", "organization", "org", "asn_org")),
      country: text(field(event, "src_endpoint.location.country", "country", "country_code")),
      firstSeenHere: text(field(event, "first_seen_here", "first_seen", "time")),
    };
    if (existing) {
      existing.failureCount += failures;
      existing.asn ||= candidate.asn;
      existing.organization ||= candidate.organization;
      existing.country ||= candidate.country;
      existing.firstSeenHere ||= candidate.firstSeenHere;
    } else {
      byIp.set(ip, candidate);
    }
  }
  return [...byIp.values()].sort(
    (left, right) => right.failureCount - left.failureCount || left.ip.localeCompare(right.ip),
  );
}

export function presentBruteForceAlert(alert: Alert): BruteForcePresentation {
  const context = record(alert.context) ?? {};
  const evidence = record(context.auth_evidence) ?? context;
  const ruleId = alert.rule_id ?? text(context.rule_id);
  const title = alert.title.toLowerCase();
  const isBruteForce =
    (ruleId ? BRUTE_FORCE_RULE_IDS.has(ruleId) : false) ||
    title.includes("brute force") ||
    title.includes("password spray") ||
    context.alert_class === "authentication_attack";

  const successValue = booleanOrNull(evidence.successful_auth);
  const successStatus: SuccessStatus =
    successValue === true ? "success" : successValue === false ? "none" : "unknown";
  const currentWindowOnly = context.current_window_only === true;
  const sources = sourceRollups(alert, currentWindowOnly);
  const usernames = Array.isArray(evidence.usernames_targeted)
    ? evidence.usernames_targeted.map(text).filter((value): value is string => Boolean(value)).slice(0, 10)
    : [];
  const authMethod = text(evidence.auth_method_accepted);
  const failureCount = sources.reduce((sum, source) => sum + source.failureCount, 0);
  const relatedDetections = Array.isArray(context.related_detections)
    ? context.related_detections.map(text).filter((value): value is string => Boolean(value))
    : ruleId
      ? [ruleId]
      : [];

  const missingFields: string[] = [];
  if (successStatus === "unknown") missingFields.push("successful_auth");
  if (!text(evidence.target_host)) missingFields.push("target_host");
  if (!text(evidence.target_service)) missingFields.push("target_service");
  if (!authMethod) missingFields.push("auth_method_accepted");
  if (usernames.length === 0 && evidence.distinct_username_count == null) {
    missingFields.push("usernames_targeted");
  }
  const trend = trendFromContext(context);
  if (trend === "unknown") missingFields.push("trend");

  return {
    isBruteForce,
    successStatus,
    successfulAccount: text(field(evidence, "successful_auth_details.account", "successful_account")),
    successfulIp: text(field(evidence, "successful_auth_details.ip", "successful_ip")),
    successfulAt: text(field(evidence, "successful_auth_details.time", "successful_at")),
    failureCount,
    sources,
    sourceCount: sources.length,
    targetHost: text(evidence.target_host),
    targetService: text(evidence.target_service),
    authMethodAccepted: authMethod,
    passwordAuthDisabled:
      evidence.password_auth_disabled === true ||
      authMethod?.toLowerCase().includes("password disabled") === true ||
      authMethod?.toLowerCase().includes("key-only") === true,
    usernames,
    distinctUsernameCount:
      evidence.distinct_username_count == null ? null : count(evidence.distinct_username_count),
    trend,
    volumeHistory: volumeHistoryFromContext(context),
    durationDays: durationDays(alert.first_seen_at, alert.last_seen_at),
    firingCount: Math.max(1, count(context.firing_count, 1)),
    currentWindowOnly,
    relatedDetections,
    missingFields,
  };
}
